import type { Asset } from '@schibsted-svp/svp-api-types';
import {
    createGetChangeFieldName,
    formatHistoryChanges,
    getHistoryChanges,
    type HistoryDataChange,
} from 'components/core/History';

const MAX_DEPTH_PATHS = [
    'category',
    'images',
    'flightTimes',
    'playback',
    'streamUrls.hls',
    'streamUrls.mp4',
    'streamConfiguration',
    'tags',
    'additional.chapters',
    'additional.featuredChapters',
    'additional.cuePoints',
    'additional.imageCaption',
    'additional.nextAsset',
    'additional.metadata',
    'additional.subtitles',
    'additional.sourceFile',
    'additional.tags',
    'additional.stories',
    'additional.access',
];

const FIELD_NAME_MAP = {
    created: 'created time',
    updated: 'updated time',
    published: 'published time',
    displays: 'views',
    'additional.chapters': 'highlights',
    'additional.cuePoints': 'ad breaks',
    'additional.settings.showInNewest': 'hide from list',
    'additional.settings.showAds': 'disable ads',
    'streamUrls.hls': 'Stream url hls',
    'streamUrls.mp4': 'Stream url mp4',
} as const;

const IGNORED_FIELDS = ['streamUrls.pseudostreaming'];

// eslint-disable-next-line no-shadow
enum HistoryAction {
    CREATED = 'created',
    PUBLISHED = 'published',
    MODIFIED = 'modified',
}

export type { HistoryDataChange };

export type HistoryData = {
    id: number;
    date: number;
    user: string;
    action: HistoryAction;
    allChanges: HistoryDataChange[];
    changes: HistoryDataChange[];
    assetData: Asset;
};

function getHistoryAction(asset: Asset, previousAsset: Asset): HistoryAction {
    if (asset.updated === asset.created) {
        return HistoryAction.CREATED;
    }
    if (asset.status === 'active' && asset.status !== previousAsset.status) {
        return HistoryAction.PUBLISHED;
    }
    return HistoryAction.MODIFIED;
}

export function prepareHistoryData(rawHistoryData: Asset[] | undefined): HistoryData[] {
    if (!rawHistoryData) {
        return [];
    }

    return rawHistoryData
        .slice()
        .sort((a, b) => b.updated - a.updated)
        .map((assetData, index, list) => {
            // previous asset is the next one on the list
            const previousAssetData = list[index + 1] || assetData;
            const { createdBy, lastEditedBy } = assetData.additional.settings || {};

            const action = getHistoryAction(assetData, previousAssetData);
            const allChanges = getHistoryChanges(previousAssetData, assetData);
            const changes = formatHistoryChanges(allChanges, {
                maxDepthPaths: MAX_DEPTH_PATHS,
                ignoredFields: IGNORED_FIELDS,
            });

            return {
                id: assetData.updated,
                date: assetData.updated,
                user: action === HistoryAction.CREATED ? createdBy : lastEditedBy,
                action,
                allChanges,
                changes,
                assetData,
            };
        });
}

export const getChangeFieldName = createGetChangeFieldName(FIELD_NAME_MAP);
